import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useGameEngine, useProviders, getGameImage, toggleSavedGame } from 'hooks/useGameEngine';
import styled, { keyframes } from 'styled-components';
import { Helmet } from 'react-helmet';
import GameImage from '../components/GameImage';
import Banner from "../components/Banner";
import { ChevronDown, SlidersHorizontal, ArrowDownNarrowWide, Play, X } from 'lucide-react';
import Cookies from 'js-cookie';
import { useQueryClient } from '@tanstack/react-query';

const PageWrapper = styled.div`
  flex: 1;
  min-width: 0;
  padding: 30px 0 24px 0;
  
  @media (min-width: 1024px) {
    padding: 0 0 32px 0;
  }
`;

const GiantHeader = styled.h1`
  font-size: 1.7rem;
  @media (min-width: 1024px) {
    font-size: 2.5rem;
  }
  font-weight: 900;
  color: #ffffff;
  margin-top: 0;
  margin-bottom: 16px;
  letter-spacing: -0.04em;
  text-transform: uppercase;
  padding: 0;
  display: flex;
  align-items: center;
`;

const SearchInputWrapper = styled.div`
  position: relative;
  padding: 0;
  margin-top: 12px;
  margin-bottom: 16px;
`;

const StyledSearchIcon = styled.div`
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: #94a6b5;
  pointer-events: none;
  display: flex;
  align-items: center;
  justify-content: center;
  
  svg {
    width: 18px;
    height: 18px;
  }
`;

const StyledSearchInput = styled.input`
  width: 100%;
  background-color: #292d2e;
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  padding: 12px 16px 12px 44px;
  color: white;
  font-size: 15px;
  font-weight: 500;
  outline: none;
  transition: border-color 0.2s;

  &:focus {
    border-color: #2f4553;
  }
  
  &::placeholder {
    color: #7f898c;
  }
`;

const FilterSortRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0;
  margin-top: 24px;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;
`;

const ControlGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const TextButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: none;
  color: #94a6b5;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: color 0.2s;

  &:hover {
    color: #ffffff;
  }
`;

const DropdownWrapper = styled.div`
  position: relative;
  display: inline-block;
`;

const PillDropdown = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: #0f212e;
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 6px;
  padding: 8px 16px;
  color: white;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: border-color 0.2s;

  &:hover {
    border-color: rgba(255, 255, 255, 0.1);
  }
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  background-color: #0F1E25;
  border: 1px solid rgba(47, 69, 83, 0.4);
  border-radius: 6px;
  width: 220px;
  max-height: 280px;
  overflow-y: auto;
  box-shadow: 0 10px 25px rgba(0,0,0,0.5);
  z-index: 50;
  padding: 4px;
`;

const DropdownItem = styled.button`
  width: 100% !important;
  text-align: left !important;
  padding: 8px 12px !important;
  border-radius: 6px !important;
  background: transparent !important;
  border: none !important;
  font-size: 14px !important;
  line-height: 20px !important;
  font-weight: 600 !important;
  color: ${props => props.active ? '#ffffff' : '#8e969f'} !important;
  background-color: ${props => props.active ? 'rgba(47, 69, 83, 0.5)' : 'transparent'} !important;
  cursor: pointer !important;
  margin: 0 !important;
  transition: all 0.15s ease !important;
  display: flex !important;
  justify-content: space-between !important;
  align-items: center !important;

  &:hover {
    background-color: rgba(47, 69, 83, 0.5) !important;
    color: #ffffff !important;
  }
`;

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  padding: 0;
  
  @media (min-width: 480px) {
    grid-template-columns: repeat(3, 1fr);
  }
  @media (min-width: 768px) {
    grid-template-columns: repeat(5, 1fr);
  }
  @media (min-width: 1024px) {
    grid-template-columns: repeat(7, 1fr);
  }
`;

const shimmer = keyframes`
  0%   { background-position: -400px 0; }
  100% { background-position: 400px 0; }
`;

const SkeletonCard = styled.div`
  background: linear-gradient(90deg, #0a1720 25%, #0f212e 50%, #0a1720 75%);
  background-size: 400px 100%;
  animation: ${shimmer} 1.4s infinite;
  border-radius: 12px;
  aspect-ratio: 3 / 4;
  border: 1px solid rgba(255,255,255,0.04);
`;

const RemoveButton = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  background: rgba(0, 0, 0, 0.7);
  border: none;
  border-radius: 50%;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ff3355;
  cursor: pointer;
  z-index: 10;
  opacity: 0;
  transition: opacity 0.2s, transform 0.2s;

  &:hover {
    transform: scale(1.15);
    background: rgba(0, 0, 0, 0.9);
  }
`;

const CardWrapper = styled.div`
  display: flex;
  flex-direction: column;
  transition: transform 0.2s;
  position: relative;
  min-width: 0;
  width: 100%;
  
  &:hover {
    transform: translateY(-2px);
  }

  &:hover ${RemoveButton} {
    opacity: 1;
  }
`;

const GameCard = styled.div`
  border-radius: 8px;
  overflow: hidden;
  position: relative;
  cursor: pointer;
  background-color: #0f212e;
  border: 1px solid rgba(255, 255, 255, 0.05);
  transition: border-color 0.2s ease;
  aspect-ratio: 3 / 4;

  &:hover {
    border-color: rgba(255, 255, 255, 0.1);
  }

  &:active {
    transform: scale(0.95);
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: fill;
    transition: transform 0.5s ease;
  }

  &:hover img {
    transform: scale(1.08);
  }
`;

const PlayOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0,0,0,0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s;

  ${GameCard}:hover & {
    opacity: 1;
  }

  .play-icon {
    font-size: 24px;
    color: white;
    background: rgba(0,0,0,0.7);
    border-radius: 50%;
    padding: 12px;
    width: 48px;
    height: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const PlayerOverlay = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
  padding: 0 4px;
`;

const ActiveDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: #25de82;
  box-shadow: 0 0 8px rgba(37, 222, 130, 0.6);
`;

const PlayerText = styled.span`
  font-size: 11px;
  font-weight: 500;
  color: white;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 90%;
`;

const LoadMoreContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-top: 32px;
  margin-bottom: 32px;
  width: 100%;
  padding: 0;
`;

const Line = styled.div`
  flex: 1;
  height: 1px;
  background-color: rgba(255, 255, 255, 0.05);
`;

const LoadMoreButton = styled.button`
  background: none;
  border: none;
  color: #9ca3af;
  font-size: 13px;
  font-weight: 900;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  cursor: pointer;
  transition: color 0.2s;

  &:hover {
    color: #ffffff;
  }
`;

const GroupPage = ({ groupSlug, hideBanner, isLobbyTab, lobbySearchQuery }) => {
  const { slug: urlSlug } = useParams();
  const slug = groupSlug || urlSlug;
  const history = useHistory();
  const queryClient = useQueryClient();

  const handleRemoveGame = async (e, gameId) => {
    e.stopPropagation();
    let uidVal = Cookies.get('uid');
    const sessionStr = Cookies.get('session');
    if (sessionStr && !uidVal) {
      try {
        let base64Url = sessionStr.split('.')[1];
        let base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        let pad = base64.length % 4;
        if (pad) {
          base64 += '='.repeat(4 - pad);
        }
        const decoded = JSON.parse(atob(base64));
        if (decoded && decoded.id) {
          uidVal = decoded.id;
        }
      } catch (err) {
        console.error('Failed to parse session token', err);
      }
    }

    if (!uidVal) return;

    try {
      const res = await toggleSavedGame(uidVal, gameId);
      if (res.success) {
        queryClient.invalidateQueries({ queryKey: ['gameEngineGames'] });
        queryClient.invalidateQueries({ queryKey: ['gameEngineProviders'] });
      }
    } catch (err) {
      console.error('Failed to remove saved game:', err);
    }
  };

  const [searchQuery, setSearchQuery] = useState('');
  const [offset, setOffset] = useState(0);
  const [gamesList, setGamesList] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const limit = 14;

  const activeSearchQuery = lobbySearchQuery !== undefined ? lobbySearchQuery : searchQuery;

  // Filtering states
  const [selectedProviderId, setSelectedProviderId] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [sortType, setSortType] = useState('Featured');
  const [sortDropdownOpen, setSortDropdownOpen] = useState(false);

  const { data: games, isLoading } = useGameEngine({
    group: slug,
    provider: selectedProviderId || undefined,
    search: activeSearchQuery || undefined,
    limit: limit,
    offset: offset
  });

  const providerRef = useRef(null);
  const sortRef = useRef(null);

  useEffect(() => {
    if (!isLobbyTab) {
      window.scrollTo(0, 0);
    }
    setOffset(0);
    setGamesList([]);
    setHasMore(true);
    setSearchQuery(''); // Reset search
    setSelectedProviderId(null); // Reset filter
    setSortType('Featured'); // Reset sort
  }, [slug, isLobbyTab]);

  // Handle resets on search/filter changes
  useEffect(() => {
    setOffset(0);
    setGamesList([]);
    setHasMore(true);
  }, [selectedProviderId, activeSearchQuery]);

  useEffect(() => {
    if (games) {
      if (offset === 0) {
        setGamesList(games);
      } else {
        setGamesList(prev => {
          const existingIds = new Set(prev.map(g => g.id));
          const newGames = games.filter(g => !existingIds.has(g.id));
          return [...prev, ...newGames];
        });
      }
      if (games.length < limit) {
        setHasMore(false);
      } else {
        setHasMore(true);
      }
    }
  }, [games, offset]);

  // Click outside listener to close dropdowns
  useEffect(() => {
    function handleClickOutside(event) {
      if (providerRef.current && !providerRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
      if (sortRef.current && !sortRef.current.contains(event.target)) {
        setSortDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Fetch all providers that have games in this group from backend
  const { data: uniqueProviders = [] } = useProviders({ group_id: slug });

  // Filter games based on search and selected provider
  const filteredGames = useMemo(() => {
    return gamesList || [];
  }, [gamesList]);

  // Sort filtered games
  const sortedGames = useMemo(() => {
    const list = [...filteredGames];
    if (sortType === 'A-Z') {
      list.sort((a, b) => a.name.localeCompare(b.name));
    } else if (sortType === 'Z-A') {
      list.sort((a, b) => b.name.localeCompare(a.name));
    }
    return list;
  }, [filteredGames, sortType]);

  const displayGames = sortedGames;

  // Mock player counts for each game
  const gamePlayers = useMemo(() => {
    if (!games) return {};
    const counts = {};
    games.forEach(game => {
      const seed = game.name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
      counts[game.id] = String(50 + (seed % 650));
    });
    return counts;
  }, [games]);

  return (
    <PageWrapper style={isLobbyTab ? { padding: 0 } : {}}>
      <Helmet>
        <title>{String(slug || '').toUpperCase()} Games</title>
      </Helmet>
      <div className={isLobbyTab ? "" : "casino-container"}>
        {!isLobbyTab && (
          <>
            <GiantHeader>
              {String(slug || '').replace(/-/g, ' ')}
            </GiantHeader>

            <SearchInputWrapper>
              <StyledSearchIcon>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <circle cx="11" cy="11" r="8"></circle>
                  <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                </svg>
              </StyledSearchIcon>
              <StyledSearchInput
                type="text"
                placeholder="Search games"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                }}
              />
            </SearchInputWrapper>

            <FilterSortRow>
              <ControlGroup>
                <TextButton>
                  <SlidersHorizontal size={16} /> Filter
                </TextButton>
                <DropdownWrapper ref={providerRef}>
                  <PillDropdown onClick={() => setDropdownOpen(!dropdownOpen)}>
                    {selectedProviderId ? uniqueProviders.find(p => String(p.id) === String(selectedProviderId))?.name : 'Providers'} <ChevronDown size={14} />
                  </PillDropdown>
                  {dropdownOpen && (
                    <DropdownMenu>
                      <DropdownItem
                        active={!selectedProviderId}
                        onClick={() => {
                          setSelectedProviderId(null);
                          setDropdownOpen(false);
                        }}
                      >
                        All Providers
                      </DropdownItem>
                      {uniqueProviders.map(provider => (
                        <DropdownItem
                          key={provider.id}
                          active={String(selectedProviderId) === String(provider.id)}
                          onClick={() => {
                            setSelectedProviderId(provider.id);
                            setDropdownOpen(false);
                          }}
                        >
                          {provider.name}
                        </DropdownItem>
                      ))}
                    </DropdownMenu>
                  )}
                </DropdownWrapper>
              </ControlGroup>
              <ControlGroup>
                <TextButton>
                  <ArrowDownNarrowWide size={16} /> Sort
                </TextButton>
                <DropdownWrapper ref={sortRef}>
                  <PillDropdown onClick={() => setSortDropdownOpen(!sortDropdownOpen)}>
                    {sortType} <ChevronDown size={14} />
                  </PillDropdown>
                  {sortDropdownOpen && (
                    <DropdownMenu style={{ left: 'auto', right: 0 }}>
                      {['Featured', 'A-Z', 'Z-A'].map(type => (
                        <DropdownItem
                          key={type}
                          active={sortType === type}
                          onClick={() => {
                            setSortType(type);
                            setSortDropdownOpen(false);
                          }}
                        >
                          {type}
                        </DropdownItem>
                      ))}
                    </DropdownMenu>
                  )}
                </DropdownWrapper>
              </ControlGroup>
            </FilterSortRow>
          </>
        )}

        {isLoading ? (
          <GridContainer>
            {Array.from({ length: 14 }).map((_, i) => (
              <SkeletonCard key={i} />
            ))}
          </GridContainer>
        ) : displayGames.length > 0 ? (
          <>
            <GridContainer>
              {displayGames.map((game, i) => (
                <CardWrapper key={i}>
                  <GameCard onClick={() => history.push(`/play/${game.id}`)}>
                    <GameImage 
                      src={getGameImage(game)} 
                      alt={game.name} 
                    />
                    <PlayOverlay>
                      <div className="play-icon">
                        <Play size={20} fill="white" color="white" />
                      </div>
                    </PlayOverlay>
                  </GameCard>
                  {slug === 'saved-games' && (
                    <RemoveButton onClick={(e) => handleRemoveGame(e, game.id)} title="Remove from saved">
                      <X size={15} color="white" strokeWidth={3} />
                    </RemoveButton>
                  )}
                  <PlayerOverlay>
                    <PlayerText>
                      {game.name}
                    </PlayerText>
                  </PlayerOverlay>
                </CardWrapper>
              ))}
            </GridContainer>

            {hasMore && (
              <LoadMoreContainer>
                <Line />
                <LoadMoreButton onClick={() => setOffset(prev => prev + limit)}>
                  LOAD MORE
                </LoadMoreButton>
                <Line />
              </LoadMoreContainer>
            )}
          </>
        ) : (
          <div style={{ color: '#9CA3AF', textAlign: 'center', padding: '50px' }}>No games found in this category.</div>
        )}
      </div>
    </PageWrapper>
  );
};

export default GroupPage;
